import {Component, OnInit, ViewContainerRef} from '@angular/core';
import {UntypedFormBuilder, UntypedFormGroup, FormGroupDirective} from '@angular/forms';
import {ActivatedRoute, Router} from '@angular/router';
import {Location} from '@angular/common';
import {MatLegacyDialog as MatDialog, MatLegacyDialogConfig as MatDialogConfig} from '@angular/material/legacy-dialog';
import {MatLegacySnackBar as MatSnackBar, MatLegacySnackBarConfig as MatSnackBarConfig} from '@angular/material/legacy-snack-bar';
import {ReplaySubject, BehaviorSubject, Observable} from 'rxjs';

import {ngcAnimations} from '@app/core/comp/animations';
import {AuthenticationService} from '@app/core/service/authentication.service';
import {ConfirmDialogComponent} from '@app/core/comp/dialog/confirm-dialog/confirm-dialog.component';
import {PASSWORD_STRENGTH_INFO} from '@app/core/utils/password-utils';

@Component({
    selector: 'ngc-set-password',
    templateUrl: './set-password.component.html',
    styleUrls  : ['./set-password.component.scss'],
    animations : ngcAnimations
})
export class SetUserPasswordComponent implements OnInit {

    passwordForm: UntypedFormGroup;
    userGuid: string;
    ts: string;
    sig: string;
    isVertical = true;
    hidePassword$ = new BehaviorSubject<boolean>(true);
    loadingChange: ReplaySubject<boolean> = new ReplaySubject<boolean>();
    public loading: Observable<boolean> = this.loadingChange.asObservable();
    changePasswordAllowed = false;
    changePasswordLinkExpired = false;
    preMessage: string;
    errorMessages: string[];
    readonly PASSWORD_STRENGTH_INFO = PASSWORD_STRENGTH_INFO;

    constructor(
        public snackBar: MatSnackBar,
        public dialog: MatDialog,
        public viewContainerRef: ViewContainerRef,
        private formBuilder: UntypedFormBuilder,
        public route: ActivatedRoute,
        public router: Router,
        private location: Location,
        private authService: AuthenticationService
    ) {
        this.route.queryParams.subscribe(params => {
            this.userGuid = params['guid'];
            this.ts = params['ts'];
            this.sig = params['sig'];

            this.location.replaceState('auth/set-password');
        });
    }

    ngOnInit() {
        this.passwordForm = this.formBuilder.group({
            password: [''],
            confirmPassword: ['']
        });

        this.checkForTimeoutOfLink();
    }

    checkForTimeoutOfLink() {
        this.authService.setupPasswordCheckSig(this.userGuid, this.ts, this.sig)
            .subscribe(
                data => {
                    if (data.existentToken) {
                        if (data.lastToken) {
                            this.changePasswordAllowed = true;
                            this.changePasswordLinkExpired = false;
                        } else {
                            this.changePasswordAllowed = false;
                            this.changePasswordLinkExpired = true;
                            this.preMessage = 'The link is already used.';
                        }
                    } else {
                        this.changePasswordAllowed = false;
                        this.changePasswordLinkExpired = false;
                        this.preMessage = 'The link has expired';
                    }
                },
                data => {
                    this.changePasswordAllowed = false;
                    this.preMessage = 'The link has expired';
                }
            );
    }

    formSubmit(formData: any, formDirective: FormGroupDirective) {
        const password = this.passwordForm.value['password'];

        this.errorMessages = null;
        this.loadingChange.next(true);
        this.authService.setupPassword(password, this.userGuid, this.ts, this.sig)
            .subscribe(
                data => {
                    this.loadingChange.next(false);
                    this.openSnackBar('Password has been set.', '');

                    const config = new MatDialogConfig();
                    config.viewContainerRef = this.viewContainerRef;
                    config.autoFocus = false;
                    const dialogRef = this.dialog.open(ConfirmDialogComponent, config);
                    dialogRef.componentInstance.item = 'error';
                    dialogRef.componentInstance.header = 'Password has been set';
                    dialogRef.componentInstance.target = 'Password has been set successfully. Back to sign in page for authentication.';
                    dialogRef.afterClosed().subscribe(result => {
                        this.router.navigate([`/login/vendor`]);
                    });
                    formDirective.resetForm();
                    this.passwordForm.reset();
                },
                error => {
                    this.loadingChange.next(false);
                    if (error != null) {
                        if (error.status === 0) {
                            this.errorMessages = ['Password has not been set.'];
                        } else if (error.error && error.error.message) {
                            this.errorMessages = error.error.message.split('.,');
                        } else if (error.message) {
                            this.errorMessages = error.message.split('.,');
                        }
                        this.openSnackBar('Password has not been set.', 'ERROR');
                    }

                    this.passwordForm.markAsPristine();
                    this.passwordForm.markAsUntouched();
                }
            );
    }

    openSnackBar(message: string, action: string) {
        this.snackBar.open(message, action, <MatSnackBarConfig>{
            duration: 3000,
        });
    }

    getNewLink() {
        this.router.navigate([`/account/forgot-password/account-setup`]);
    }
}
